/**
 * AddToCart — click delegation for `[data-action=add-to-cart]` buttons.
 * Performs `POST /cart/add.js`, dispatches `product:added` on success,
 * shows inline error UI on failure.
 *
 * Extracted from global.js. Used by product/collection modules and as a
 * document-level fallback for product cards rendered by async sections.
 *
 * Loaded as an ES module from `layout/theme.liquid` and imported by feature
 * modules through the `@theme/cart-api` importmap alias. Attaches
 * `window.AddToCart` for backwards-compat consumers.
 *
 * Depends on:
 *   - @theme/delegate
 *   - @theme/form
 *   - window.routes (theme.liquid inline)
 *   - window.theme (theme.liquid inline)
 *
 */

import { Delegate } from '@theme/delegate';
import { Form } from '@theme/form';

function resolveProductForm(addToCartButton) {
  const closestForm = addToCartButton.closest('form');
  if (closestForm) return closestForm;

  const formId = addToCartButton.getAttribute('form');
  if (!formId) return null;

  const linkedForm = document.getElementById(formId);
  return linkedForm instanceof HTMLFormElement ? linkedForm : null;
}

function resolveUpsellScope(addToCartButton, formElement) {
  return (
    addToCartButton.closest('product-component') ||
    formElement?.closest('product-component') ||
    formElement
  );
}

export function dispatchCartUpdated(cart) {
  const count = Number(cart?.item_count);
  if (!Number.isFinite(count)) return;

  window.theme = window.theme || {};
  window.theme.cartCount = count;

  document.dispatchEvent(
    new CustomEvent('cart:updated', {
      detail: {
        cart,
        count,
      },
    })
  );
}

export async function updateCartLine(lineKey, quantity, options = {}) {
  const response = await fetch(`${window.routes.localeRootUrl}/cart/change.js`, {
    body: JSON.stringify({
      id: lineKey,
      quantity,
    }),
    credentials: 'same-origin',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error('Cart change failed');
  }

  const cart = await response.json();
  dispatchCartUpdated(cart);
  return cart;
}

export class AddToCart {
  #pageLoaderResetTimeout = null;

  constructor(element) {
    this.element = element;
    this.delegateElement = new Delegate(this.element);
    this.pageLoader = document.body.querySelector(".pageLoader");
    this.bindEvents();
  }

  bindEvents() {
    this.delegateElement.on(
      "click",
      "[data-action=add-to-cart]",
      this.addToCart.bind(this)
    );

    this.delegateElement.on(
      "submit",
      "form",
      this.addToCartFromSubmit.bind(this)
    );
  }

  destroy() {
    this.delegateElement?.off?.();
    this.delegateElement = null;

    if (this.#pageLoaderResetTimeout) {
      clearTimeout(this.#pageLoaderResetTimeout);
      this.#pageLoaderResetTimeout = null;
    }
  }

  addToCartFromSubmit(event, formElement) {
    const addToCartButton = formElement.querySelector("[data-action=add-to-cart]");
    if (!addToCartButton) return;

    this.addToCart(event, addToCartButton);
  }

  addToCart(event, addToCartButton) {
    if (event.defaultPrevented) return;

    event.preventDefault();
    if (addToCartButton.hasAttribute("disabled")) return;

    const formElement = resolveProductForm(addToCartButton);
    if (!formElement) return;

    const upsellScope = resolveUpsellScope(addToCartButton, formElement);

    if (this.pageLoader) this.pageLoader.style.width = '100%';
    addToCartButton.setAttribute("disabled", "disabled");
    formElement.querySelector('[name="id"]')?.removeAttribute("disabled");

    const formHelper = new Form();
    const serialized = formHelper.serialize(formElement);

    const mainVariantId = Number(serialized.id);
    const mainQuantity = Number(serialized.quantity) || 1;
    const selectedUpsellIds = Array.from(
      upsellScope.querySelectorAll('[data-upsell-checkbox]:checked')
    )
      .map((input) => Number(input.value))
      .filter((id) => Number.isFinite(id) && id > 0);

    let requestBody = serialized;
    if (selectedUpsellIds.length) {
      const items = [{ id: mainVariantId, quantity: mainQuantity }];
      selectedUpsellIds.forEach((id) => {
        if (id === mainVariantId) return;
        items.push({ id, quantity: 1 });
      });
      requestBody = { items };
    }

    fetch(window.routes.localeRootUrl + "/cart/add.js", {
      body: JSON.stringify(requestBody),
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    }).then(function (response) {
      if (response.ok) {
        var inlineErr = this.element.querySelector(".ProductForm--Error");
        if (inlineErr) {
          inlineErr.textContent = "";
          inlineErr.classList.remove("visible");
        }
        if (window.theme.cartMode === 'page') {
          location.href = window.routes.cart_page_url;
          return;
        }
        if (window.location.href.indexOf("cart") != -1) {
          location.reload();
          return;
        }
        addToCartButton.removeAttribute("disabled");
        this.element.dispatchEvent(
          new CustomEvent("product:added", {
            bubbles: true,
            detail: {
              variant: mainVariantId,
              quantity: mainQuantity,
            },
          })
        );
        fetch(window.routes.localeRootUrl + "/cart.js", {
          credentials: "same-origin",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        })
          .then(function (cartResponse) {
            if (!cartResponse.ok) return null;
            return cartResponse.json();
          })
          .then(function (cart) {
            if (cart) dispatchCartUpdated(cart);
          });
      } else {
        response.json().then(function (content) {
          var inlineErr = this.element.querySelector(".ProductForm--Error");
          if (inlineErr) {
            var apiText =
              typeof content?.description === "string"
                ? content.description
                : typeof content?.message === "string"
                  ? content.message
                  : "";
            inlineErr.textContent = apiText || "";
            inlineErr.classList.add("visible");
            setTimeout(function () {
              inlineErr.classList.remove("visible");
            }, 2500);
            this.resetPageLoader();
            addToCartButton.removeAttribute("disabled");
          } else {
            const errorMessageElement = document.createElement("span");
            errorMessageElement.className = "u-p4 accent";
            errorMessageElement.textContent = content["description"];
            addToCartButton.removeAttribute("disabled");
            addToCartButton.insertAdjacentElement(
              "afterend",
              errorMessageElement
            );
            setTimeout(function () {
              errorMessageElement.remove();
            }, 2500);
            this.resetPageLoader();
          }
        }.bind(this));
      }
    }.bind(this));
  }

  resetPageLoader() {
    if (!this.pageLoader) return;

    if (this.#pageLoaderResetTimeout) {
      clearTimeout(this.#pageLoaderResetTimeout);
      this.#pageLoaderResetTimeout = null;
    }

    this.pageLoader.style.left = 'unset';
    this.pageLoader.style.right = '0px';
    this.pageLoader.style.width = '0%';
    // timeout: pageLoader CSS transition completes before resetting anchor side
    this.#pageLoaderResetTimeout = setTimeout(() => {
      this.#pageLoaderResetTimeout = null;
      if (!this.pageLoader) return;
      this.pageLoader.style.left = '0px';
      this.pageLoader.style.right = 'unset';
    }, 550);
  }
}

function initGlobalAddToCart() {
  if (!document.body || document.body.dataset.addToCartBound === "true") return;

  document.body.dataset.addToCartBound = "true";
  new AddToCart(document.body);
}

// Bridge for classic-script consumers (global.js). Removed in Faza 5.
window.AddToCart = AddToCart;

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initGlobalAddToCart, { once: true });
} else {
  initGlobalAddToCart();
}
